package br.com.heyagenda.ui.components

import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.input.OffsetMapping
import androidx.compose.ui.text.input.TransformedText
import androidx.compose.ui.text.input.VisualTransformation
import br.com.heyagenda.domain.util.PhoneUtils

/**
 * VisualTransformation for Brazilian phone numbers (landline and mobile).
 * Formats raw digits (e.g. "11987654321") into "(11) 98765-4321" visually,
 * ensuring cursor tracking remains smooth and never moves backward or jumps to the left.
 */
class PhoneVisualTransformation : VisualTransformation {

    override fun filter(text: AnnotatedString): TransformedText {
        val raw = text.text.filter { it.isDigit() }.take(11)
        val formatted = PhoneUtils.formatDigits(raw)

        val offsetMapping = object : OffsetMapping {
            override fun originalToTransformed(offset: Int): Int {
                val clamped = offset.coerceIn(0, raw.length)
                if (clamped == 0) return 0

                var digitCount = 0
                var targetTransformedIndex = 0

                for (i in formatted.indices) {
                    if (formatted[i].isDigit()) {
                        digitCount++
                        if (digitCount == clamped) {
                            targetTransformedIndex = i + 1
                            // Include any subsequent non-digit mask characters (like ") " or "-")
                            while (targetTransformedIndex < formatted.length && !formatted[targetTransformedIndex].isDigit()) {
                                targetTransformedIndex++
                            }
                            break
                        }
                    }
                }
                return targetTransformedIndex.coerceIn(0, formatted.length)
            }

            override fun transformedToOriginal(offset: Int): Int {
                val clamped = offset.coerceIn(0, formatted.length)
                var digitCount = 0
                for (i in 0 until clamped) {
                    if (formatted[i].isDigit()) {
                        digitCount++
                    }
                }
                return digitCount.coerceIn(0, raw.length)
            }
        }

        return TransformedText(AnnotatedString(formatted), offsetMapping)
    }
}
